import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Bell, AlertTriangle, TrendingUp, Target, Zap } from "lucide-react";
import { getTriggers, updateSettings } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Overline, Badge, Loader } from "../components/Primitives";
import { POS } from "../lib/format";

function Toggle({ on, onClick, testid, label }) {
  return (
    <button data-testid={testid} onClick={onClick} className="flex items-center gap-2 group">
      <span className="relative w-9 h-5 rounded-full transition-colors" style={{ background: on ? "#00e5ff" : "#2d3c59" }}>
        <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" style={{ left: on ? "18px" : "2px" }} />
      </span>
      <span className="text-sm text-[#94a3b8] group-hover:text-[#f8fafc]">{label}</span>
    </button>
  );
}

const TYPE_META = {
  rebalance: { icon: AlertTriangle, color: "#ffb300", label: "Rebalance" },
  signal: { icon: Zap, color: "#00e5ff", label: "Factor Signal" },
  target: { icon: Target, color: "#00e676", label: "Price Target" },
};
const SEV = { high: "#ff3b30", medium: "#ffb300", low: "#64748b" };

export default function Triggers() {
  const { macro } = useApp();
  const { data, isLoading } = useQuery({ queryKey: ["triggers"], queryFn: getTriggers, refetchInterval: 45000 });
  const [settings, setSettings] = useState({ email_digest: true, email_recommendations: false });
  useEffect(() => { if (data?.settings) setSettings(data.settings); }, [data]);
  const toggle = async (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    try { await updateSettings(next); } catch { setSettings(settings); }
  };
  if (isLoading || !data) return <Loader label="Evaluating triggers…" />;

  const triggers = data.triggers || [];
  const groups = {};
  triggers.forEach((t) => {
    const d = (t.date || "").slice(0, 10);
    (groups[d] = groups[d] || []).push(t);
  });
  const dates = Object.keys(groups).sort().reverse();
  const counts = { rebalance: 0, signal: 0, target: 0 };
  triggers.forEach((t) => { counts[t.type] = (counts[t.type] || 0) + 1; });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4"><Overline>Total Triggers</Overline><div className="font-mono text-2xl font-semibold mt-1">{triggers.length}</div></Card>
        <Card className="p-4"><Overline>Rebalance</Overline><div className="font-mono text-2xl font-semibold mt-1 text-[#ffb300]">{counts.rebalance}</div></Card>
        <Card className="p-4"><Overline>Factor Signals</Overline><div className="font-mono text-2xl font-semibold mt-1 text-[#00e5ff]">{counts.signal}</div></Card>
        <Card className="p-4"><Overline>Price Targets</Overline><div className="font-mono text-2xl font-semibold mt-1 text-[#00e676]">{counts.target}</div></Card>
      </div>

      <Card className="p-4" testid="digest-settings">
        <div className="flex items-start gap-3 mb-3">
          <Bell className="w-4 h-4 text-[#00e5ff] mt-0.5" />
          <span className="text-sm text-[#94a3b8]">A daily email is sent to your login address every morning (8:00 IST). Set price targets from the Portfolio tab.</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 pl-7">
          <Toggle testid="toggle-digest" on={settings.email_digest} onClick={() => toggle("email_digest")} label="Email daily trigger digest" />
          <Toggle testid="toggle-recos" on={settings.email_recommendations} onClick={() => toggle("email_recommendations")} label="Include daily Buy/Sell recommendations" />
        </div>
      </Card>

      {triggers.length === 0 ? (
        <Card className="p-12 text-center" testid="triggers-empty">
          <Bell className="w-8 h-8 text-[#2d3c59] mx-auto mb-3" />
          <div className="text-sm text-[#94a3b8]">No triggers yet.</div>
          <div className="text-xs text-[#64748b] mt-1">Triggers fire automatically as your holdings drift, factor signals change, or price targets are hit.</div>
        </Card>
      ) : dates.map((d) => (
        <div key={d} data-testid={`trigger-day-${d}`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="font-mono text-xs text-[#94a3b8]">{new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</div>
            <div className="h-px flex-1 bg-[#1e293b]" />
            <span className="font-mono text-[11px] text-[#64748b]">{groups[d].length} event{groups[d].length > 1 ? "s" : ""}</span>
          </div>
          <div className="space-y-2">
            {groups[d].map((t, i) => {
              const meta = TYPE_META[t.type] || TYPE_META.signal;
              const Icon = meta.icon;
              return (
                <motion.div key={t.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}>
                  <Card className="p-3.5 flex items-start gap-3" testid={`trigger-${t.id}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}1a` }}>
                      <Icon className="w-4 h-4" style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-[#f8fafc]">{t.title}</span>
                        <Badge color={meta.color}>{meta.label}</Badge>
                        <span className="font-mono text-[10px]" style={{ color: SEV[t.severity] }}>● {t.severity}</span>
                      </div>
                      <div className="text-xs text-[#94a3b8] mt-1">{t.detail}</div>
                    </div>
                    <div className="font-mono text-[10px] text-[#64748b] flex-shrink-0">{new Date(t.date).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
